import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { 
  ArrowLeft, 
  Upload, 
  Download, 
  RefreshCw, 
  Trash2, 
  FileSpreadsheet,
  CheckCircle,
  AlertCircle,
  Clock,
  Plus,
  X
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PricingManagement = ({ user }) => {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [uploading, setUploading] = useState({});
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newPricelist, setNewPricelist] = useState({
    key: '',
    display_name: '',
    filename: '',
    required_columns: ''
  });
  const [addingPricelist, setAddingPricelist] = useState(false);
  const fileInputRefs = useRef({});

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchData = async () => {
    try {
      const [filesRes, statusRes] = await Promise.all([
        axios.get(`${API}/admin/pricing/files`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/pricing/status`, { headers: getAuthHeaders() })
      ]);
      setFiles(filesRes.data);
      setStatus(statusRes.data);
      setError("");
    } catch (err) {
      setError("Failed to fetch pricing data");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'zkteco_admin') {
      fetchData();
    }
  }, [user]);

  const handleUpload = async (fileKey, file) => {
    if (!file) return;
    
    setUploading(prev => ({ ...prev, [fileKey]: true }));
    setError("");
    setSuccess("");

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API}/admin/pricing/upload/${fileKey}`,
        formData,
        { 
          headers: { 
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      setSuccess(response.data.message);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(prev => ({ ...prev, [fileKey]: false }));
      // Reset file input
      if (fileInputRefs.current[fileKey]) {
        fileInputRefs.current[fileKey].value = '';
      }
    }
  };

  const handleDelete = async (fileKey) => {
    if (!window.confirm("Delete uploaded file and revert to default pricing?")) return;
    
    setError("");
    setSuccess("");

    try {
      const response = await axios.delete(
        `${API}/admin/pricing/upload/${fileKey}`,
        { headers: getAuthHeaders() }
      );
      setSuccess(response.data.message);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || "Delete failed");
    }
  };

  const handleDeletePricelist = async (fileKey, displayName) => {
    if (!window.confirm(`Delete the entire "${displayName}" pricelist? This cannot be undone.`)) return;
    
    setError("");
    setSuccess("");

    try {
      const response = await axios.delete(
        `${API}/admin/pricing/pricelist/${fileKey}`,
        { headers: getAuthHeaders() }
      );
      setSuccess(response.data.message);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || "Delete failed");
    }
  };

  // Built-in pricelists that cannot be deleted
  const builtinPricelists = ['comet_speedlane', 'atlas', 'full_height_turnstiles', 'half_height_turnstiles', 'parking_barriers', 'bio_cvsecurity', 'biocv_access_control', 'biocvsecurity_hardware', 'time_attendance', 'visitor_management', 'securest'];

  const handleAddPricelist = async () => {
    if (!newPricelist.key || !newPricelist.display_name || !newPricelist.filename || !newPricelist.required_columns) {
      setError("Please fill in all fields");
      return;
    }

    setAddingPricelist(true);
    setError("");

    try {
      const columns = newPricelist.required_columns.split(',').map(c => c.trim()).filter(c => c);
      
      await axios.post(
        `${API}/admin/pricing/add-pricelist`,
        {
          key: newPricelist.key.toLowerCase().replace(/\s+/g, '_'),
          display_name: newPricelist.display_name,
          filename: newPricelist.filename.endsWith('.csv') ? newPricelist.filename : `${newPricelist.filename}.csv`,
          required_columns: columns
        },
        { headers: getAuthHeaders() }
      );
      
      setSuccess(`Pricelist "${newPricelist.display_name}" added successfully`);
      setShowAddDialog(false);
      setNewPricelist({ key: '', display_name: '', filename: '', required_columns: '' });
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to add pricelist");
    } finally {
      setAddingPricelist(false);
    }
  };

  const handleDownload = async (fileKey) => {
    try {
      const response = await axios.get(
        `${API}/admin/pricing/download/${fileKey}`,
        { 
          headers: getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      const file = files.find(f => f.key === fileKey);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file?.filename || 'pricing.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError("Download failed");
    }
  };

  const handleApplyChanges = async () => {
    if (!window.confirm("Apply all pending pricing changes? This will reload pricing data for all configurators.")) return;
    
    setApplying(true);
    setError("");
    setSuccess("");

    try {
      const response = await axios.post(
        `${API}/admin/pricing/apply`,
        {},
        { headers: getAuthHeaders() }
      );
      
      if (response.data.success) {
        setSuccess(`${response.data.message} Reloaded: ${response.data.reloaded_files.join(', ')}`);
      } else {
        setError(response.data.message);
      }
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || "Apply failed");
    } finally {
      setApplying(false);
    }
  };

  const getStatusBadge = (file) => {
    if (file.status === 'pending') {
      return (
        <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
          <Clock className="w-3 h-3 mr-1" />
          Pending Apply
        </Badge>
      );
    }
    if (file.source === 'uploaded') {
      return (
        <Badge className="bg-blue-100 text-blue-800 border-blue-200">
          <CheckCircle className="w-3 h-3 mr-1" />
          Custom Upload
        </Badge>
      );
    }
    return (
      <Badge className="bg-slate-100 text-slate-800 border-slate-200">
        Default
      </Badge>
    );
  };

  if (user?.role !== 'admin' && user?.role !== 'zkteco_admin') {
    return (
      <div className="flex items-center justify-center h-screen">
        <Alert variant="destructive">
          <AlertDescription>Admin access required</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  const hasPendingChanges = status?.pending_changes || files.some(f => f.status === 'pending');

  return (
    <div className="space-y-6 p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Pricing Management</h1>
          <p className="text-slate-600 mt-1">Upload and manage pricing CSV files</p>
        </div>
        <Button
          variant="outline"
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Dashboard
        </Button>
      </div>

      {/* Apply Changes Banner */}
      {hasPendingChanges && (
        <Alert className="bg-yellow-50 border-yellow-200">
          <AlertCircle className="h-4 w-4 text-yellow-600" />
          <AlertDescription className="flex items-center justify-between">
            <span className="text-yellow-800 font-medium">
              You have pending pricing changes. Click "Apply Changes" to make them live.
            </span>
            <Button
              onClick={handleApplyChanges}
              disabled={applying}
              className="bg-yellow-600 hover:bg-yellow-700 text-white ml-4"
            >
              {applying ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Applying...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Apply Changes
                </>
              )}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Messages */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {success && (
        <Alert className="bg-green-50 border-green-200">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">{success}</AlertDescription>
        </Alert>
      )}

      {/* Current Status */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Current Pricing Status</CardTitle>
          <CardDescription>Products currently loaded in each configurator</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-5 gap-4">
            <div className="bg-green-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-green-700">{status?.comet_products || 0}</div>
              <div className="text-sm text-green-600">Comet Speedlane</div>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-700">{status?.atlas_products || 0}</div>
              <div className="text-sm text-blue-600">Atlas Access Control</div>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-orange-700">{status?.fht_products || 0}</div>
              <div className="text-sm text-orange-600">Full Height Turnstiles</div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-purple-700">{status?.barriers_products || 0}</div>
              <div className="text-sm text-purple-600">Parking Barriers</div>
            </div>
            <div className="bg-pink-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-pink-700">{status?.biocvsecurity_products || 0}</div>
              <div className="text-sm text-pink-600">Bio CVSecurity</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pricing Files */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5" />
                Pricing Files
              </CardTitle>
              <CardDescription>
                Upload new CSV files to update pricing. Changes are applied immediately on upload.
              </CardDescription>
            </div>
            <Button
              onClick={() => setShowAddDialog(true)}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Pricelist
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {files.map((file) => (
              <div 
                key={file.key} 
                className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <FileSpreadsheet className="w-8 h-8 text-green-600" />
                    <div>
                      <div className="font-medium text-slate-900">{file.display_name}</div>
                      <div className="text-sm text-slate-500">
                        {file.filename} • {file.product_count} products
                      </div>
                      {file.uploaded_at ? (
                        <div className="flex items-center gap-1.5 mt-1 text-xs text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full w-fit">
                          <Clock className="w-3 h-3" />
                          Last uploaded: {new Date(file.uploaded_at).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}{file.uploaded_by ? ` by ${file.uploaded_by}` : ''}
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 mt-1 text-xs text-slate-400 bg-slate-50 px-2 py-0.5 rounded-full w-fit">
                          <Clock className="w-3 h-3" />
                          Default pricelist (never uploaded)
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  {getStatusBadge(file)}
                  
                  {/* Download Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownload(file.key)}
                    title="Download current CSV"
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                  
                  {/* Upload Button */}
                  <div className="relative">
                    <input
                      type="file"
                      accept=".csv"
                      ref={el => fileInputRefs.current[file.key] = el}
                      onChange={(e) => handleUpload(file.key, e.target.files?.[0])}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      disabled={uploading[file.key]}
                    />
                    <Button
                      variant="default"
                      size="sm"
                      disabled={uploading[file.key]}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {uploading[file.key] ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-1" />
                          Upload
                        </>
                      )}
                    </Button>
                  </div>
                  
                  {/* Delete Button - for uploaded files (revert) or custom pricelists (remove) */}
                  {file.source === 'uploaded' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(file.key)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      title="Delete uploaded file and revert to default"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                  
                  {/* Delete entire custom pricelist */}
                  {!builtinPricelists.includes(file.key) && file.source === 'default' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeletePricelist(file.key, file.display_name)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      title="Delete this pricelist"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Instructions</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-600 space-y-2">
          <p><strong>1. Download</strong> - Click the download button to get the current CSV file as a template.</p>
          <p><strong>2. Edit</strong> - Open in Excel/Google Sheets, make your pricing changes, and save as CSV.</p>
          <p><strong>3. Upload</strong> - Click Upload and select your modified CSV file.</p>
          <p><strong>4. Apply Changes</strong> - Click the "Apply Changes" button to make your new pricing live.</p>
          <p className="text-yellow-700 bg-yellow-50 p-2 rounded mt-4">
            <strong>Note:</strong> Changes are staged until you click "Apply Changes". This allows you to upload multiple files before making them all live at once.
          </p>
        </CardContent>
      </Card>

      {/* Add Pricelist Dialog */}
      {showAddDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Add New Pricelist</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAddDialog(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="display_name">Display Name *</Label>
                <Input
                  id="display_name"
                  placeholder="e.g., Intercoms Pricing"
                  value={newPricelist.display_name}
                  onChange={(e) => setNewPricelist({...newPricelist, display_name: e.target.value})}
                />
              </div>
              
              <div>
                <Label htmlFor="key">Key (unique identifier) *</Label>
                <Input
                  id="key"
                  placeholder="e.g., intercoms"
                  value={newPricelist.key}
                  onChange={(e) => setNewPricelist({...newPricelist, key: e.target.value.toLowerCase().replace(/\s+/g, '_')})}
                />
                <p className="text-xs text-slate-500 mt-1">Lowercase letters, numbers, and underscores only</p>
              </div>
              
              <div>
                <Label htmlFor="filename">CSV Filename *</Label>
                <Input
                  id="filename"
                  placeholder="e.g., intercoms_pricing.csv"
                  value={newPricelist.filename}
                  onChange={(e) => setNewPricelist({...newPricelist, filename: e.target.value})}
                />
              </div>
              
              <div>
                <Label htmlFor="required_columns">Required Columns (comma-separated) *</Label>
                <Input
                  id="required_columns"
                  placeholder="e.g., SKU, Description, Price"
                  value={newPricelist.required_columns}
                  onChange={(e) => setNewPricelist({...newPricelist, required_columns: e.target.value})}
                />
                <p className="text-xs text-slate-500 mt-1">These columns must exist in uploaded CSVs</p>
              </div>
              
              <div className="flex gap-2 pt-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowAddDialog(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  onClick={handleAddPricelist}
                  disabled={addingPricelist}
                >
                  {addingPricelist ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    'Add Pricelist'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingManagement;
